import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { transformImage } from '@/components/asset-manager/components/image-crop-modal/api';
import type { CropData } from '@/components/asset-manager/components/image-crop-modal/types';
import type { Asset } from '@/components/asset-manager/types';
import type { UseImageCreatorAssistantReturn } from '@/hooks/mastra/use-image-creator-assistant';
import { buildAiGeneratedAssetUploadParams, imageUploadSubject } from '@/lib/ai-asset-upload';
import { showAiCreditsExhaustedModal, useAiCreditsExhausted } from '@/lib/ai-credits-gate';
import http from '@/lib/http';
import { cmsAssetCropperPath, urlUploadCmsAssetsPath } from '@/lib/routes';
import type { ConversationItem, ImageConversationItem } from '../types';

export interface EditedImageParams {
  sourceId: string;
  imageUrl: string;
  prompt: string;
}

interface UseAiImageAssistantEditorOptions {
  assistant?: UseImageCreatorAssistantReturn;
  conversation: ConversationItem[];
  onImageEdited?: (params: EditedImageParams) => void;
}

export function useAiImageAssistantEditor({
  assistant,
  conversation,
  onImageEdited,
}: UseAiImageAssistantEditorOptions) {
  const [expiredImageIds, setExpiredImageIds] = useState<Set<string>>(new Set());
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isSavingCrop, setIsSavingCrop] = useState(false);
  const [cropImage, setCropImage] = useState<ImageConversationItem | null>(null);
  const creditsExhausted = useAiCreditsExhausted();

  const lastHandledEditorSeqRef = useRef(assistant?.openEditorRequest ?? 0);
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const pendingEditorAdjustmentsRef = useRef(assistant?.pendingEditorAdjustments);
  pendingEditorAdjustmentsRef.current = assistant?.pendingEditorAdjustments;

  const markImageExpired = useCallback(
    (id: string) => setExpiredImageIds((prev) => new Set([...prev, id])),
    [],
  );

  const closeCropModal = useCallback(() => setIsCropModalOpen(false), []);

  const openCropModalForImage = useCallback((image: ImageConversationItem) => {
    setCropImage(image);
    setIsCropModalOpen(true);
  }, []);

  const handleDirectTransform = useCallback(
    async (image: ImageConversationItem, operation: 'upscale' | 'remove-bg') => {
      if (!assistant) return;
      if (creditsExhausted) {
        showAiCreditsExhaustedModal();
        return;
      }
      try {
        const resultUrl = await transformImage(image.imageUrl, operation);
        const newId = assistant.addEditedImageToConversation(image.id, resultUrl);
        const subject = imageUploadSubject(image);
        await assistant.saveEditedImageToThread(resultUrl, image.prompt, subject);
        onImageEdited?.({
          sourceId: newId,
          imageUrl: resultUrl,
          prompt: image.prompt,
        });
      } catch (error) {
        assistant.removeTransformPlaceholder();
        if (axios.isCancel(error)) return;

        toast.error('Image transform failed. Please try again.');
      }
    },
    [assistant, onImageEdited, creditsExhausted],
  );
  const handleDirectTransformRef = useRef(handleDirectTransform);
  handleDirectTransformRef.current = handleDirectTransform;

  useEffect(() => {
    if (!assistant) return;
    const { openEditorRequest } = assistant;
    if (openEditorRequest === 0) return;
    if (openEditorRequest === lastHandledEditorSeqRef.current) return;
    lastHandledEditorSeqRef.current = openEditorRequest;

    const adj = pendingEditorAdjustmentsRef.current;
    const lastImg = [...conversationRef.current].reverse().find((i) => i.type === 'image') as
      | ImageConversationItem
      | undefined;

    if (!lastImg) return;

    if (adj?.directTransform) {
      void handleDirectTransformRef.current(lastImg, adj.directTransform);
      return;
    }

    openCropModalForImage(lastImg);
  }, [assistant?.openEditorRequest, assistant, openCropModalForImage]);

  const saveCrop = useCallback(
    async (cropData: CropData) => {
      if (!cropImage || !assistant) return;
      setIsSavingCrop(true);
      try {
        const uploadSourceUrl = cropData.imageUrl || cropImage.imageUrl;
        const subject = imageUploadSubject(cropImage);
        const uploadResponse = await http.post(
          urlUploadCmsAssetsPath(),
          buildAiGeneratedAssetUploadParams(uploadSourceUrl, subject),
        );
        let savedAsset = uploadResponse.data as Asset;

        const hasTransforms =
          cropData.rotation !== 0 ||
          cropData.flipHorizontal ||
          cropData.flipVertical ||
          (cropData.croppedAreaPixels.width > 0 && cropData.croppedAreaPixels.height > 0);

        if (hasTransforms) {
          const cropResponse = await http.post<Asset>(cmsAssetCropperPath(savedAsset.id), {
            crop: cropData.croppedAreaPixels,
            rotation: cropData.rotation,
            flipHorizontal: cropData.flipHorizontal,
            flipVertical: cropData.flipVertical,
          });
          savedAsset = cropResponse.data;
        }

        setIsCropModalOpen(false);
        const savedUrl = savedAsset.url ?? uploadSourceUrl;
        const newId = assistant.addEditedImageToConversation(cropImage.id, savedUrl);
        setCropImage((prev: ImageConversationItem | null) =>
          prev ? { ...prev, id: newId, imageUrl: savedUrl } : null,
        );
        await assistant.saveEditedImageToThread(savedUrl, cropImage.prompt, subject);
        onImageEdited?.({
          sourceId: newId,
          imageUrl: savedUrl,
          prompt: cropImage.prompt,
        });
      } catch {
        toast.error('Failed to save cropped image. Please try again.');
      } finally {
        setIsSavingCrop(false);
      }
    },
    [assistant, cropImage, onImageEdited],
  );

  return {
    closeCropModal,
    cropImage,
    expiredImageIds,
    isCropModalOpen,
    isSavingCrop,
    markImageExpired,
    openCropModalForImage,
    saveCrop,
  };
}
